// Вкладка «Шаги»: график с целью Garmin ступенькой и сводкой периода, серии и рекорды,
// календарь за год (万 — дни от 10 000), ритм недели. Всё считается на клиенте из дневного ряда.

import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'

import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart'
import { Card, EmptyState, ErrorText, Spinner, Stat } from '@/components/ui'
import { formatDate, formatDayTitle, formatNumber, pluralRu } from '@/lib/format'
import { periodRange, todayIso, type Period } from '@/lib/periods'
import { useSeries } from '@/pages/metrics/api'
import { dayPoints } from '@/pages/metrics/series'
import { useGarminStatus } from '@/pages/settings/garmin/api'
import { WAN_STEPS } from '@/pages/today/logic'

import {
  calendar,
  goalMet,
  monthLabel,
  periodSummary,
  records,
  RHYTHM_WEEKS,
  stepDays,
  streaks,
  weekdayDative,
  weekdayRhythm,
  WEEKDAY_SHORT,
  type CalendarCell,
  type StepDay,
} from './steps'

const ALL_DAYS = { start: null, bucket: 'day' } as const
const DAYS: [string, string, string] = ['день', 'дня', 'дней']
const STEPS: [string, string, string] = ['шаг', 'шага', 'шагов']

const steps = (n: number) => `${formatNumber(n, { digits: 0 })} ${pluralRu(n, STEPS)}`
const days = (n: number) => `${n} ${pluralRu(n, DAYS)}`

function ChartCard({ list, goalPoints, current, today }: {
  list: StepDay[]
  goalPoints: ReturnType<typeof dayPoints>
  current: number | null
  today: string
}) {
  const [period, setPeriod] = useState<Period>('month')
  const range = periodRange(period, today)
  const summary = periodSummary(list, range.start, range.end)
  const points = useMemo(() => list.map((d) => ({ x: d.day, y: d.steps })), [list])
  const goal = goalPoints.length
    ? { steps: goalPoints, label: 'цель Garmin' }
    : current
      ? { value: current, label: 'цель' }
      : null
  return (
    <Card title="Шаги по дням">
      <TimeSeriesChart
        series={[{ key: 'steps', label: 'Шаги', unit: 'шагов', digits: 0, points }]}
        goal={goal}
        period={period}
        onPeriodChange={setPeriod}
        periods={['week', 'month', '3m', 'year']}
        height={240}
        today={today}
        emptyText="Шагов за период нет"
      />
      <div className="mt-4 grid grid-cols-3 gap-4 border-t border-border pt-4">
        <Stat label="В среднем" value={formatNumber(summary.average, { digits: 0 })} hint="шагов в день" />
        <Stat
          label="Всего"
          value={formatNumber(summary.total, { digits: 0 })}
          hint={summary.days ? `за ${days(summary.days)}` : 'нет данных'}
        />
        <Stat
          label="Цель"
          value={summary.days ? `${summary.goalDays} из ${summary.days}` : '—'}
          hint={summary.days ? `${pluralRu(summary.days, ['дня', 'дней', 'дней'])} с целью` : null}
        />
      </div>
    </Card>
  )
}

function RecordsCard({ list, today }: { list: StepDay[]; today: string }) {
  const s = streaks(list, today)
  const r = records(list)
  const todayDay = list.find((d) => d.day === today)
  const currentHint = s.current === 0
    ? todayDay && !goalMet(todayDay)
      ? `сегодня до цели ${steps(todayDay.goal - todayDay.steps)}`
      : 'выполни цель сегодня — начнётся новая'
    : s.todayIncluded
      ? 'включая сегодня'
      : 'сегодня ещё можно продлить'
  return (
    <Card title="Серии и рекорды" subtitle="Серия — дни подряд с выполненной целью">
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3">
        <Stat label="Текущая серия" value={s.current} unit={pluralRu(s.current, DAYS)} hint={currentHint} />
        <Stat
          label="Рекорд серии"
          value={s.best?.length ?? 0}
          unit={pluralRu(s.best?.length ?? 0, DAYS)}
          hint={s.best ? `${formatDate(s.best.from)} — ${formatDate(s.best.to)}` : 'ещё впереди'}
        />
        <Stat
          label="Лучший день"
          value={formatNumber(r.day?.steps, { digits: 0 })}
          hint={r.day ? formatDate(r.day.day) : null}
        />
        <Stat
          label="Лучшая неделя"
          value={formatNumber(r.week?.steps, { digits: 0 })}
          hint={r.week ? `с ${formatDate(r.week.start)}` : null}
        />
        <Stat
          label="Лучший месяц"
          value={formatNumber(r.month?.steps, { digits: 0 })}
          hint={r.month ? monthLabel(r.month.month) : null}
        />
        <Stat label="Всего" value={formatNumber(r.total, { digits: 0 })} hint={`шагов за ${days(list.length)}`} />
      </div>
    </Card>
  )
}

const LEVEL_FILL = ['var(--surface-2)', 'color-mix(in srgb, var(--accent) 28%, transparent)', 'color-mix(in srgb, var(--accent) 52%, transparent)', 'color-mix(in srgb, var(--accent) 78%, transparent)', 'var(--accent)']

function cellText(cell: CalendarCell): string {
  const title = formatDayTitle(cell.day)
  if (cell.steps === null) return `${title}: шагов нет`
  const goal = cell.goal ? ` · цель ${formatNumber(cell.goal, { digits: 0 })}` : ''
  return `${title}: ${steps(cell.steps)}${goal}${cell.wan ? ' · 万' : ''}`
}

function Cell({ cell }: { cell: CalendarCell }) {
  if (cell.future) return <span className="size-3" />
  return (
    <span
      data-day={cell.day}
      title={cellText(cell)}
      className="relative flex size-3 items-center justify-center rounded-[3px]"
      style={{ background: LEVEL_FILL[cell.level] }}
    >
      {cell.wan ? <span className="size-1 rounded-full bg-accent-fg" /> : null}
    </span>
  )
}

function CalendarCard({ list, today }: { list: StepDay[]; today: string }) {
  const cal = useMemo(() => calendar(list, today), [list, today])
  const [selected, setSelected] = useState(today)
  const scroller = useRef<HTMLDivElement>(null)
  // На телефоне год не влезает — сразу показываем последние недели; подписи дней недели прилипают слева.
  useEffect(() => {
    const el = scroller.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [])
  const cells = cal.weeks.flat()
  const chosen = cells.find((c) => c.day === selected) ?? null
  const pick = (e: MouseEvent<HTMLDivElement>) => {
    const day = (e.target as HTMLElement).closest<HTMLElement>('[data-day]')?.dataset.day
    if (day) setSelected(day)
  }
  return (
    <Card
      title="Календарь за год"
      subtitle={
        cal.days
          ? `Цель выполнена ${days(cal.goalDays)} из ${cal.days}; 万 — ${days(cal.wanDays)} от ${formatNumber(WAN_STEPS, { digits: 0 })} шагов`
          : 'За последний год шагов нет'
      }
    >
      <div ref={scroller} className="-mx-1 overflow-x-auto px-1 pb-1">
        <div className="inline-grid grid-cols-[auto_1fr] gap-x-1.5" onClick={pick}>
          <span />
          <div className="relative h-4 text-[10px] text-muted" aria-hidden="true">
            {cal.months.map((m) => (
              <span key={`${m.column}-${m.label}`} className="absolute top-0" style={{ left: m.column * 15 }}>
                {m.label}
              </span>
            ))}
          </div>
          <div className="sticky left-0 z-10 grid grid-rows-7 gap-[3px] bg-surface pr-1 text-[10px] leading-3 text-muted" aria-hidden="true">
            {WEEKDAY_SHORT.map((d, i) => (
              <span key={d}>{i % 2 === 0 ? d : ''}</span>
            ))}
          </div>
          <div
            role="img"
            aria-label={`Календарь шагов за год: цель выполнена ${days(cal.goalDays)} из ${cal.days}`}
            className="grid grid-flow-col grid-rows-7 gap-[3px]"
          >
            {cells.map((c) => (
              <Cell key={c.day} cell={c} />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-muted">
        <p aria-live="polite" className="text-sm text-fg">{chosen ? cellText(chosen) : null}</p>
        <div className="flex items-center gap-1.5">
          <span>меньше</span>
          {LEVEL_FILL.map((fill) => (
            <span key={fill} className="size-3 rounded-[3px]" style={{ background: fill }} />
          ))}
          <span>больше цели</span>
          <span className="ml-2 flex size-3 items-center justify-center rounded-[3px] bg-accent">
            <span className="size-1 rounded-full bg-accent-fg" />
          </span>
          <span>万</span>
        </div>
      </div>
    </Card>
  )
}

function RhythmCard({ list, today }: { list: StepDay[]; today: string }) {
  const rhythm = weekdayRhythm(list, today)
  const max = Math.max(1, ...rhythm.days.map((d) => d.average ?? 0))
  const most = rhythm.most === null ? null : rhythm.days[rhythm.most]!
  const least = rhythm.least === null ? null : rhythm.days[rhythm.least]!
  return (
    <Card title="Ритм недели" subtitle={`Среднее по дням недели за ${RHYTHM_WEEKS} недель`}>
      <div className="flex h-36 items-end gap-2" role="list" aria-label="Среднее по дням недели">
        {rhythm.days.map((d, i) => (
          <div
            key={d.label}
            role="listitem"
            aria-label={`${d.label}: ${d.average === null ? 'нет данных' : steps(d.average)}`}
            className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
          >
            <span className="text-[11px] tabular-nums text-muted">
              {d.average === null ? '—' : formatNumber(d.average / 1000, { digits: 1, fixed: true })}
            </span>
            <div
              className={i === rhythm.least ? 'w-full rounded-t-md bg-accent/40' : 'w-full rounded-t-md bg-accent'}
              style={{ height: `${((d.average ?? 0) / max) * 100}%`, minHeight: d.average ? 2 : 0 }}
            />
            <span className="text-xs text-muted">{d.label}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-sm text-muted">
        {most && least && rhythm.most !== null && rhythm.least !== null
          ? `Больше всего ходишь ${weekdayDative(rhythm.most)} (${steps(most.average!)}), меньше всего — ${weekdayDative(rhythm.least)} (${steps(least.average!)}). Над столбцами — тысячи шагов.`
          : 'Ритм появится, когда наберутся шаги хотя бы за два разных дня недели.'}
      </p>
    </Card>
  )
}

export function StepsPage() {
  const today = todayIso()
  const stepsSeries = useSeries('steps', ALL_DAYS)
  const goalSeries = useSeries('steps_goal', ALL_DAYS)
  const status = useGarminStatus()
  const current = status.data?.steps_goal ?? null
  const list = useMemo(
    () => stepDays(stepsSeries.data?.points ?? [], goalSeries.data?.points ?? [], current),
    [stepsSeries.data, goalSeries.data, current],
  )
  const goalPoints = useMemo(() => dayPoints(goalSeries.data?.points ?? []), [goalSeries.data])

  if (stepsSeries.isPending || goalSeries.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  if (stepsSeries.error) return <ErrorText error={stepsSeries.error} />
  if (list.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="activity"
          title="Шагов пока нет"
          description="Шаги и цель на день придут из Garmin после синхронизации."
          action={<Link to="/settings/garmin" className="text-sm text-accent">Настройки Garmin</Link>}
        />
      </Card>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      <ChartCard list={list} goalPoints={goalPoints} current={current} today={today} />
      <RecordsCard list={list} today={today} />
      <CalendarCard list={list} today={today} />
      <RhythmCard list={list} today={today} />
    </div>
  )
}
